import * as yup from "yup";
import {
  ManagementField,
  ManagementFieldRequiredIn,
} from "../types/client.type";
import { BRIEFING_STATUS_OPTIONS } from "./briefingStatus";

/** Slots fixos do relatório de Dados Cadastrais: Campo Gerencial 1..21 e Dado 1..21. */
export const MANAGEMENT_FIELD_SLOT_COUNT = 21;

export function sortManagementFields(
  fields: ManagementField[] = []
): ManagementField[] {
  return [...fields].sort((a, b) => a.order - b.order);
}

/** Campos cadastrados antes da flag "Export Cliente?" existir contam como "Sim". */
export function isExportedToClient(field: ManagementField): boolean {
  return field.export_client !== false;
}

/**
 * Momento em que o campo obrigatório é exigido. Obrigatórios cadastrados antes
 * da opção "Obrigatório em" existir continuam valendo nos Dados Cadastrais.
 */
export function getRequiredIn(
  field: ManagementField
): ManagementFieldRequiredIn | undefined {
  if (!field.required) return undefined;
  return field.required_in ?? ManagementFieldRequiredIn.REGISTRATION;
}

/** Remove a configuração de obrigatoriedade que não se aplica ao campo. */
export function normalizeManagementFieldRequirement(
  field: ManagementField
): ManagementField {
  const requiredIn = getRequiredIn(field);
  const { required_in, required_status, ...rest } = field;
  if (!requiredIn) return { ...rest, required: false };
  if (requiredIn === ManagementFieldRequiredIn.REGISTRATION) {
    return { ...rest, required: true, required_in: requiredIn };
  }
  return { ...rest, required: true, required_in: requiredIn, required_status };
}

export function getProjectStatusText(status?: string): string {
  return (
    BRIEFING_STATUS_OPTIONS.find((option) => option.value === status)?.text ??
    status ??
    ""
  );
}

/** Texto da coluna "Obrigatório em" da listagem. */
export function requiredInLabel(field: ManagementField): string {
  const requiredIn = getRequiredIn(field);
  if (requiredIn === ManagementFieldRequiredIn.REGISTRATION) {
    return "Dados Cadastrais";
  }
  if (requiredIn === ManagementFieldRequiredIn.STATUS) {
    return getProjectStatusText(field.required_status) || "-";
  }
  return "-";
}

/**
 * Schema dos valores de Campos Gerenciais do evento: campos obrigatórios nos
 * Dados Cadastrais precisam estar preenchidos. Os obrigatórios por status só
 * são cobrados na troca de status (getPendingManagementFieldsForStatus).
 */
export function managementFieldValuesSchema(fields: ManagementField[] = []) {
  const shape: Record<string, yup.StringSchema> = {};
  fields
    .filter(
      (field) => getRequiredIn(field) === ManagementFieldRequiredIn.REGISTRATION
    )
    .forEach((field) => {
      shape[field.name] = yup
        .string()
        .trim()
        .required(`${field.name} é obrigatório`);
    });
  return yup.object(shape);
}

/** Campos obrigatórios em `status` que ainda estão sem preenchimento. */
export function getPendingManagementFieldsForStatus(
  fields: ManagementField[] = [],
  values: Record<string, string> = {},
  status?: string
): string[] {
  if (!status) return [];
  return sortManagementFields(fields)
    .filter(
      (field) =>
        getRequiredIn(field) === ManagementFieldRequiredIn.STATUS &&
        field.required_status === status
    )
    .filter((field) => !String(values?.[field.name] ?? "").trim())
    .map((field) => field.name);
}
